import React, { useState, useEffect } from "react"
import RefreshStatus from "../../../icons/refreshStatusdp.svg"
import TrendUp from "../../../icons/trendPositive.svg"
import ADF from "../../../icons/adf.svg"
import {
  deepDiveAsync,
  deepDiveScreenChange,
  flowDiagramAsync,
  gantActiveSelector,
  gantChartStatusSelector,
  selectedRunDetailsSelector,
  updateFlowDiagram,
} from "../MonitorSlice"
import { useAppDispatch, useAppSelector } from "../../../util/hooks"
import CircularProgress from "@mui/material/CircularProgress"
import Box from "@mui/material/Box"
import { createUseStyles, useTheme } from "react-jss"
import Legend from "../../../components/Legend"
import { Screen } from "../../../util/interface"
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material"
import CustomDivider from "../../../components/CustomDivider"
import { ADF_LINK, GANTT_OPTIONS, NOTE_TEXT } from "../../../util/StaticData"
import GantChart from "./GantChart"
import { Note } from "../../../components/Note"
import { header } from "../../../util/commonstyles"

const useStyles = createUseStyles({
  container: {
    height: "100%",
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    textAlign: "base",
    justifyContent: "space-between",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
  },
  refreshIcon: {
    marginRight: 16,
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
  },
  noteText: {
    display: "flex",
    flexDirection: "column",
  },
  headerLabel: {
    marginTop: 14,
    paddingRight: 10,
    ...header,
  },
  controls: {
    padding: "2px 8px",
    alignItems: "center",
    gap: "0px",
    borderRadius: "2px",
    // background: "#E21824",
    color: "#FFF",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "24px",
    letterSpacing: "0.44px",
    display: "flex",
    justifyContent: "space-between",
    marginTop: 10,
  },
  radioContainer: {
    display: "flex",
    flexDirection: "row",
  },
  radioBox: {
    color: "#333",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "8px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
    display: "flex",
  },
  radioLabel: {
    color: "#333",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "8px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
  },
  adfLink: {
    border: "1px solid #2FD4CF",
    borderRadius: "7px",
    padding: "2px",
    height: "23px",
    width: "31px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "auto",
  },
  legendContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "stretch",
    width: "40%",
    height: 32,
    marginLeft: 300,
  },
  gantChartContainer: {
    height: "100%",
  },
  customLabel: {
    color: "#333 !important",
    fontFamily: "Montserrat !important",
    fontSize: "12px !important",
    fontStyle: "normal !important",
    fontWeight: "400 !important",
    lineHeight: "150% !important",
    height: 18,
  },
  subHeaderText: {
    display: "flex",
    alignItems: "flex-end",
  },
})

const GantChartWrapper = function () {
  const dispatch = useAppDispatch()
  const theme: any = useTheme()
  const classes = useStyles({ theme })

  const activeGantSelection = useAppSelector(gantActiveSelector)
  const status: any = useAppSelector(gantChartStatusSelector)
  const activeRunDetails = useAppSelector(selectedRunDetailsSelector)

  // Trigger actions when activeRunDetails changes
  useEffect(() => {
    try {
      if (activeGantSelection?.["extra"] && activeRunDetails) {
        const id = activeRunDetails["id"]
        selectionChange(activeGantSelection?.["extra"]?.["name"], id)
      }
    } catch (error) {
      console.error(error)
    }
  }, [activeRunDetails])

  // Handle selection change and dispatch necessary actions
  const selectionChange = (name: string, id: number) => {
    dispatch(updateFlowDiagram(name))
    dispatch(deepDiveAsync({ pipeLineName: name, id }))
    dispatch(flowDiagramAsync({ reference_pipeline: "" }))
    dispatch(deepDiveScreenChange(Screen.Default))
  }

  const legendData = [
    { name: "Completed", color: "#2C7E98" },
    { name: "In progress", color: "#27AF8D" },
    { name: "Scheduled", color: "#F6BB42" },
    { name: "Failed", color: "#E21824" },
  ]

  // State to manage time period selection
  const [activeTimePeriodOption, setActiveTimePeriodOption] = useState(
    GANTT_OPTIONS.WEEKLY,
  )

  // Handle time period option change
  const handleChange = (event: any) => {
    setActiveTimePeriodOption(event.target.value)
  }

  return (
    <div className={classes.container}>
      {/* Header section */}
      <div className={classes.headerContainer}>
        <div className={classes.headerTitle}>
          <img
            className={classes.refreshIcon}
            src={RefreshStatus}
            alt="Refresh Status"
          />
          <div className={classes.headerText}>
            <div className={classes.subHeaderText}>
              <h1 className={classes.headerLabel}>Refresh Status​ Calendar</h1>
              <img
                className={classes.refreshIcon}
                src={TrendUp}
                alt="Trend up"
              />
            </div>
            <Note note={NOTE_TEXT.REFRESH_CALENDAR} />
          </div>
        </div>

        {/* Controls for time period options */}
        <div className={classes.controls}>
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="options"
              name="options"
              value={activeTimePeriodOption}
              onChange={handleChange}
            >
              <div className={classes.radioBox}>
                <FormControlLabel
                  value={GANTT_OPTIONS.DAILY}
                  control={
                    <Radio
                      sx={{
                        color: "#2FD4CF",
                        "&.Mui-checked": {
                          color: "#2FD4CF",
                        },
                      }}
                    />
                  }
                  label={GANTT_OPTIONS.DAILY}
                  classes={{ label: classes.customLabel }}
                />
                <FormControlLabel
                  value={GANTT_OPTIONS.WEEKLY}
                  control={
                    <Radio
                      sx={{
                        color: "#2FD4CF",
                        "&.Mui-checked": {
                          color: "#2FD4CF",
                        },
                      }}
                    />
                  }
                  label={GANTT_OPTIONS.WEEKLY}
                  classes={{ label: classes.customLabel }}
                />
              </div>
            </RadioGroup>
          </FormControl>

          <span className={classes.adfLink}>
            <a href={ADF_LINK} target="blank" rel="noopener noreferrer">
              <img src={ADF} alt="ADF Link" />
            </a>
          </span>
        </div>
      </div>

      {/* Divider */}
      <CustomDivider />

      {/* Gantt chart section */}
      <div className={classes.gantChartContainer}>
        {/* Legend for chart statuses */}
        <div className={classes.legendContainer}>
          {legendData.map((items: any) => (
            <div key={items.name}>
              <Legend name={items.name} color={items.color} />
            </div>
          ))}
        </div>
        {/* Display loading spinner or Gantt chart */}
        {status === "loading" ? (
          <Box className="flex h-full items-center justify-center">
            <CircularProgress />
          </Box>
        ) : (
          <GantChart viewMode={activeTimePeriodOption} />
        )}
      </div>
    </div>
  )
}

export default GantChartWrapper
